import React, { useState, useEffect } from "react";
import client from "../../lib/api";
import { mediaSrc } from "../../lib/media";
import { STAGES, fmtGbp } from "../../mock";
import { useCities } from "../../hooks/useCities";
import { ScoreRing } from "../../components/widgets";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { GripVertical, MapPin } from "lucide-react";
import { toast } from "sonner";
import { tr } from "../../i18n/LangContext";

export default function Pipeline() {
  const [deals, setDeals] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [cityFilter, setCityFilter] = useState("all");
  const { cities: cityList } = useCities();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await client.get("/properties");
        setDeals(res.data.filter((d) => d.disposition !== "disqualified"));
      } catch (e) {
        toast.error(tr("Couldn't load pipeline"));
      }
    };
    load();
  }, []);

  const onDrop = async (stageId) => {
    if (!dragId) return;
    const moved = deals.find((d) => d.id === dragId);
    const current = dragId;
    setDragId(null);
    if (!moved || moved.stage === stageId) return;
    setDeals((prev) => prev.map((d) => (d.id === current ? { ...d, stage: stageId } : d)));
    const stage = STAGES.find((s) => s.id === stageId);
    try {
      await client.put(`/properties/${current}`, { stage: stageId });
      toast.success(`${moved.title} → ${stage.label}`);
    } catch (e) {
      setDeals((prev) => prev.map((d) => (d.id === current ? { ...d, stage: moved.stage } : d)));
      toast.error(tr("Couldn't move deal"));
    }
  };

  const shown = cityFilter === "all"
    ? deals
    : deals.filter((d) => d.city === cityFilter || !d.city);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-white">{tr("Property CRM")}</h2>
          <p className="text-xs text-muted-foreground">{tr("Drag deals through your acquisition pipeline")}{cityFilter !== "all" ? ` · ${cityFilter} (${shown.length})` : ""}</p>
        </div>
        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="h-10 w-[190px] border-white/12 bg-white/[0.03] text-white" data-testid="property-pipeline-city-filter">
            <MapPin className="h-4 w-4 text-muted-foreground" /><SelectValue />
          </SelectTrigger>
          <SelectContent className="glass-strong border-white/10">
            <SelectItem value="all">{tr("All cities")}</SelectItem>
            {(cityList || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
      {STAGES.map((stage) => {
        const items = shown.filter((d) => d.stage === stage.id);
        const total = items.reduce((s, d) => s + d.askGbp, 0);
        return (
          <div key={stage.id} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(stage.id)}
            className="flex w-[280px] shrink-0 flex-col rounded-2xl border border-white/[0.06] bg-card/30">
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: stage.color }} />
                <span className="text-sm font-semibold text-white">{tr(stage.label)}</span>
                <span className="rounded-full bg-white/5 px-1.5 text-xs text-muted-foreground">{items.length}</span>
              </div>
              <span className="text-[11px] text-muted-foreground">{fmtGbp(total)}</span>
            </div>
            <div className="flex-1 space-y-3 p-3 min-h-[120px]">
              {items.map((d) => (
                <div key={d.id} draggable onDragStart={() => setDragId(d.id)} onDragEnd={() => setDragId(null)}
                  className={`group cursor-grab rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 transition-all hover:border-white/15 active:cursor-grabbing ${dragId === d.id ? "opacity-40" : ""}`}>
                  <div className="flex items-start gap-3">
                    <img src={mediaSrc(d.image)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-white">{d.title}</p>
                      <p className="text-xs text-muted-foreground">{d.district}</p>
                      {d.city && <p className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-brand-green" data-testid="deal-city-badge"><MapPin className="h-3 w-3" />{d.city}</p>}
                    </div>
                    <GripVertical className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-display text-sm font-semibold text-white">{fmtGbp(d.askGbp)}</span>
                    <ScoreRing score={d.score} size={36} stroke={4} />
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <div className="grid h-24 place-items-center rounded-xl border border-dashed border-white/8 text-xs text-muted-foreground">{tr("Drop deals here")}</div>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
